<template>
  <section class="page">
    <div class="page__header">
      <h2 class="page__title">{{ $t('kps.planExtract.title') }}</h2>
    </div>

    <div class="section-card">
      <DataTable
          :columns="visibleColumns"
          :rows="rows"
          :rowKey="rowKey"
          :headerHeight="56"
          :rowHeight="64"
      >
        <template #cell-idx="{ row }">
          <div v-if="!row.editing" class="cell-text">{{ row.idx }}</div>
          <input v-else class="tbl-input" v-model="row.idx" />
        </template>

        <template #cell-activity="{ row }">
          <div v-if="!row.editing" class="cell-text">{{ row.activity }}</div>
          <input v-else class="tbl-input" v-model="row.activity" />
        </template>

        <template #cell-deadline="{ row }">
          <div v-if="!row.editing" class="cell-text">{{ row.deadline }}</div>
          <DatePicker v-else v-model="row.deadline" />
        </template>

        <template #cell-responsible="{ row }">
          <div v-if="!row.editing" class="cell-text">{{ row.responsible }}</div>
          <SelectField v-else v-model="row.responsible_id" :options="users" :placeholder="$t('common.chooseOption')" />
        </template>

        <template #cell-note="{ row }">
          <div v-if="!row.editing" class="cell-text">{{ row.note }}</div>
          <input v-else class="tbl-input" v-model="row.note" />
        </template>

        <template #head-actions>
          <div class="tbl__header-text" style="justify-content: flex-end; text-align: right; width: 100%;"></div>
        </template>

        <template #cell-actions="{ row }">
          <div v-if="!readOnly" class="actions">
            <button
                v-if="!row.editing"
                class="actions__btn actions__btn--edit"
                @click="onEdit(row)"
                :title="$t('common.edit')"
                :aria-label="$t('common.edit')"
            >
              <img class="actions__icon" src="../../assets/images/edit.svg" alt="edit" />
            </button>
            <template v-else>
              <button
                  class="actions__btn actions__btn--accept"
                  @click="onAccept(row)"
                  :title="$t('common.save')"
                  :aria-label="$t('common.save')"
              >
                <img class="actions__icon" src="../../assets/images/accept.svg" alt="accept" />
              </button>
              <button
                  class="actions__btn actions__btn--decline"
                  @click="onDecline(row)"
                  :title="$t('common.cancel')"
                  :aria-label="$t('common.cancel')"
              >
                <img class="actions__icon" src="../../assets/images/decline.svg" alt="decline" />
              </button>
            </template>
            <button
                class="actions__btn actions__btn--delete"
                @click="onDelete(row)"
                :title="$t('common.delete')"
                :aria-label="$t('common.delete')"
            >
              <img class="actions__icon" src="../../assets/images/markdown/trash.svg" alt="delete" />
            </button>
          </div>
        </template>
      </DataTable>

      <div style="margin-top: 8px; display: flex; justify-content: flex-end; padding: 0 12px 12px;">
        <ActionButton v-if="!readOnly" :color="'#2A8A1B'" :text="$t('kps.planExtract.addRecord')" @click="addRow" />
      </div>
    </div>

    <div class="page__footer">
      <ViewNavButtons
          :prev-text="$t('common.back')"
          :next-text="$t('common.next')"
          :prev-to="{ path: '/kps/dd-members' }"
          :next-to="{ path: '/kps/border-violations' }"
      />
    </div>

    <transition name="toast-slide">
      <div v-if="showToast" class="toast" :class="toastMessage.includes('✗') ? 'toast-error' : 'toast-success'">
        <div class="toast-content">{{ toastMessage }}</div>
      </div>
    </transition>
  </section>
</template>

<script>
import DataTable from '@/components/table/DataTable.vue'
import ViewNavButtons from '@/components/navigation/ViewNavButtons.vue'
import ActionButton from '@/components/widgets/ActionButton.vue'
import MaskedInput from '@/components/widgets/MaskedInput.vue'
import SelectField from '@/components/widgets/SelectField.vue'
import DatePicker from '@/components/widgets/DatePicker.vue'
import {checkOrCreateBookByDate, resolveBookIdForDate} from '@/api/book'
import { createPlanExtractItem, updatePlanExtractItem, deletePlanExtractItem } from '@/api/planExtract'
import { http } from '@/api/http'

import { ensureCurrentUser, getCachedCurrentUser } from '@/api/user'
import { canEdit } from '@/access/armAccess'

export default {
  name: 'KpsPlanExtractView',
  components: { DataTable, ViewNavButtons, MaskedInput, SelectField, DatePicker , ActionButton },
  data() {
    return {
      currentUser: null,
      rows: [],
      bookId: null,
      bookContext: null,
      loading: false,
      error: null,
      users: [],

      toastMessage: '',
      showToast: false,
    }
  },
  computed: {
    canEditKps() {
      return canEdit(this.currentUser || getCachedCurrentUser(), '/kps/section-4')
    },
    readOnly() { return !this.canEditKps },
    columns() {
      return [
        { key: 'idx', label: this.$t('kps.planExtract.colIdx'), width: 73 },
        { key: 'activity', label: this.$t('kps.planExtract.colActivity'), width: 420 },
        { key: 'deadline', label: this.$t('kps.planExtract.colDeadline'), width: 160 },
        { key: 'responsible', label: this.$t('kps.planExtract.colResponsible'), width: 226 },
        { key: 'actions', label: '', width: 150 },
      ]
    },
    visibleColumns() {
      return this.readOnly ? this.columns.filter(c => c.key !== 'actions') : this.columns;
    },
  },
  async mounted() {
    try { this.currentUser = await ensureCurrentUser() }
    catch { this.currentUser = getCachedCurrentUser() }
    this.initAndFetch()
  },
  methods: {
    rowKey(r) {
      return r.id
    },

    showSuccessToast(message) {
      this.toastMessage = message
      this.showToast = true
      clearTimeout(this.__toastTimer)
      this.__toastTimer = setTimeout(() => { this.showToast = false }, 2500)
    },
    showErrorToast(message) {
      this.toastMessage = message
      this.showToast = true
      clearTimeout(this.__toastTimer)
      this.__toastTimer = setTimeout(() => { this.showToast = false }, 3500)
    },
    validationMessageFromError(e) {
      if (!e || e.status !== 400 || !e.payload || typeof e.payload !== 'object') return ''

      const labels = {
        item_number: this.$t('kps.planExtract.reqIdx'),
        description: this.$t('kps.planExtract.reqActivity'),
        due_date: this.$t('kps.planExtract.reqDeadline'),
        responsible_id: this.$t('kps.planExtract.reqResponsible'),
      }

      const missing = []
      for (const [k, v] of Object.entries(e.payload)) {
        const msg = Array.isArray(v) ? v.join(' ') : String(v || '')
        if (/required|обязател|blank|пуст/i.test(msg)) missing.push(labels[k] || k)
      }
      if (missing.length) return `${this.$t('common.fillRequiredMany')}: ${missing.join(', ')}`
      return ''
    },

    async initAndFetch() {
      try {
        this.loading = true
        const d = new Date()
        const two = (n) => (n < 10 ? `0${n}` : String(n))
        const dateISO = `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}`
        
        // Получаем полный контекст книги
        const bookCtx = await checkOrCreateBookByDate(dateISO)
        this.bookContext = bookCtx
        this.bookId = bookCtx?.id || bookCtx?.book?.id
        
        await this.fetchRows()
        await this.fetchUsers()
      } catch (e) {
        console.error('PlanExtract: init error', e)
      } finally {
        this.loading = false
      }
    },

    async ensureBookId() {
      return this.bookId
    },

    async fetchRows() {
      if (!this.bookId) return
      try {
        // Берем events из контекста
        const list = this.bookContext?.events || []
        if (Array.isArray(list)) {
          this.rows = list.map((it) => {
            const dueRaw = it.due_date ?? it.deadline ?? ''
            const displayDeadline = this.formatIsoToDisplay(dueRaw)
            return {
              id: it.id,
              idx: it.item_number ?? it.idx ?? '',
              activity: it.description ?? it.activity ?? '',
              deadline: displayDeadline,
              responsible: (it.responsible && typeof it.responsible === 'object') ? (it.responsible.full_name || '') : (it.responsible ?? it.responsible_name ?? ''),
              responsible_id: it.responsible_id ?? ((it.responsible && typeof it.responsible === 'object') ? it.responsible.id : null),
              note: it.note ?? '',
              is_complete: it.is_complete ?? false,
              editing: false,
            }
          })
        }
      } catch (e) {
        console.error('PlanExtract: failed to load list', e)
      }
    },

    async fetchUsers() {
      try {
        const response = await http.get('users/')
        const list = response.data
        if (Array.isArray(list)) {
          this.users = list.map(u => ({
            value: u.id,
            label: u.full_name,
            ...u
          }))
        }
      } catch (e) {
        console.error('Users: list failed', e)
      }
    },

    addRow() {
      const row = {
        __id: 'pex-' + Date.now(),
        idx: '',
        activity: '',
        deadline: '',
        responsible: '',
        responsible_id: null,
        note: '',
        is_complete: false,
        editing: true,
      }
      this.rows = [...this.rows, row]
    },

    onEdit(row) {
      row.__backup = JSON.parse(JSON.stringify(row))
      row.editing = true
    },

    async onAccept(row) {
      try {
        if (!String(row.idx || '').trim()) {
          this.showErrorToast(`${this.$t('common.fillRequired')}: ${this.$t('kps.planExtract.reqIdx')}`)
          return
        }
        const { __id, __backup, editing, ...rest } = row
        const payload = this.buildPayload(row)

        if (row.id) {
          await updatePlanExtractItem(row.id, payload)
        } else {
          const bid = await this.ensureBookId()
          const resp = await createPlanExtractItem({ book: bid, ...payload })
          if (resp && resp.id) row.id = resp.id
        }

        row.editing = false
        delete row.__backup

        // Update responsible display name after save
        if (row.responsible_id) {
          const user = this.users.find(u => u.value === row.responsible_id)
          if (user) row.responsible = user.label
        }

        this.showSuccessToast(this.$t('common.saved'))

        void rest
      } catch (e) {
        console.error('PlanExtract: save failed', e)
        this.showErrorToast(this.validationMessageFromError(e) || (this.$t('common.saveFailed') + ': ' + (e?.message || e)))
      }
    },

    onDecline(row) {
      if (row.__backup) {
        const b = row.__backup
        Object.keys(b).forEach((k) => {
          if (k !== '__backup') row[k] = b[k]
        })
        delete row.__backup
      }
      row.editing = false
    },

    async onDelete(row) {
      const id = row.__id ?? row.id
      if (!row.id) {
        const idx = this.rows.findIndex((r) => (r.__id ?? r.id) === id)
        if (idx !== -1) this.rows = [...this.rows.slice(0, idx), ...this.rows.slice(idx + 1)]
        return
      }
      try {
        await deletePlanExtractItem(row.id)
        const idx = this.rows.findIndex((r) => (r.__id ?? r.id) === id)
        if (idx !== -1) this.rows = [...this.rows.slice(0, idx), ...this.rows.slice(idx + 1)]
        this.showSuccessToast(this.$t('common.deleted'))
      } catch (e) {
        console.error('PlanExtract: delete failed', e)
        if (e?.status === 404) {
          const idx = this.rows.findIndex((r) => (r.__id ?? r.id) === id)
          if (idx !== -1) this.rows = [...this.rows.slice(0, idx), ...this.rows.slice(idx + 1)]
          this.showSuccessToast(this.$t('common.deleted'))
          return
        }
        this.showErrorToast(this.$t('common.deleteFailed') + ': ' + (e?.message || e))
      }
    },

    formatIsoToDisplay(dateStr) {
      if (!dateStr) return ''
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [y, m, d] = dateStr.split('-')
        return `${d}.${m}.${y}`
      }
      return dateStr
    },

    formatDisplayToIso(dateStr) {
      if (!dateStr) return ''
      if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateStr)) {
        const [d, m, y] = dateStr.split('.')
        return `${y}-${m}-${d}`
      }
      return dateStr
    },
    formatToDateInput(dateStr) {
      // Конвертирует ДД.ММ.ГГГГ -> ГГГГ-ММ-ДД
      if (!dateStr) return '';
      if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateStr)) {
        const [day, month, year] = dateStr.split('.');
        return `${year}-${month}-${day}`;
      }
      return dateStr;
    },
    formatFromDateInput(dateStr) {
      // Конвертирует ГГГГ-ММ-ДД -> ДД.ММ.ГГГГ
      if (!dateStr) return '';
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [year, month, day] = dateStr.split('-');
        return `${day}.${month}.${year}`;
      }
      return dateStr;
    },
    buildPayload(row) {
      const isoDate = this.formatDisplayToIso(row.deadline)
      return {
        item_number: row.idx || '',
        description: row.activity || '',
        due_date: isoDate || null,
        responsible_id: row.responsible_id ?? null,
        is_complete: row.is_complete ?? false,
      }
    },
  },
}
</script>

<style scoped>
.page {
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.page__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.page__title {
  margin: 0;
  color: #111827;
  font-size: 24px;
  font-weight: 700;
  line-height: 31.2px;
}

.section-card {
  position: relative;
  background: #ffffff;
  border-radius: 16px;
  padding: 0;
}

.page__footer {
  display: flex;
  justify-content: flex-end;
  margin-top: auto;
}

.cell-text {
  flex: 1 1 0;
  color: #111827;
  font-size: 16px;
  line-height: 20px;
  width: 100%;
}
.tbl-input {
  width: 100%;
  height: 32px;
  padding: 6px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 16px;
  line-height: 20px;
  font-family: 'Times New Roman', Times, serif;
  color: #111827;
  background: #fff;
}
.tbl-input:focus {
  outline: 2px solid rgba(47, 120, 238, 0.25);
  outline-offset: 0;
}

/* Prevent date value truncation in narrow cells (Chrome). */
.tbl-input[type="date"] {
  min-width: 150px;
}

/* Toast Notifications */
.toast {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9999;
  min-width: 220px;
  max-width: 420px;
  padding: 14px 16px;
  border-radius: 12px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
}
.toast-success { background: #2A8A1B; }
.toast-error { background: #D81717; }
.toast-content {
  color: #fff;
  font-weight: 600;
  font-size: 14px;
  line-height: 18px;
}
.toast-slide-enter-active,
.toast-slide-leave-active {
  transition: all 0.3s ease;
}
.toast-slide-enter-from {
  transform: translateY(20px);
  opacity: 0;
}
.toast-slide-leave-to {
  transform: translateY(20px);
  opacity: 0;
}

.actions {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  justify-content: flex-end;
  width: 100%;
  flex-wrap: wrap;
}
.actions__btn {
  width: 30px;
  height: 30px;
  padding: 6px;
  border-radius: 8px;
  border: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.actions__icon {
  width: 16px;
  height: 16px;
  display: block;
}
.actions__btn--edit {
  background: #2f78ee;
}
.actions__btn--accept {
  background: #2a8a1b;
}
.actions__btn--decline {
  background: #d81717;
}
.actions__btn--delete {
  background: #d81717;
}

</style>
